import ContentLoadingState from "@/components/ContentLoadingState";
import { useParams, Link, useLocation, useSearchParams, Navigate } from "@/lib/router-compat";
import { uiT } from "@/i18n/ui";
import type { SiteLang } from "@/hooks/useLanguage";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogSEO from "@/components/blog/BlogSEO";
import { publicBlogSlug } from "@/lib/publicBlogSlug";
import BlogPostHero from "@/components/blog/BlogPostHero";
import ReadingProgressBar from "@/components/blog/ReadingProgressBar";
import TableOfContents from "@/components/blog/TableOfContents";
import ShareButtons from "@/components/blog/ShareButtons";
import RelatedPosts from "@/components/blog/RelatedPosts";
import NewsletterSignup from "@/components/blog/NewsletterSignup";
import AuthorBioBox from "@/components/blog/AuthorBioBox";
import ListenButton from "@/components/blog/ListenButton";
import InlineCTA from "@/components/blog/InlineCTA";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import BlogFAQ from "@/components/blog/BlogFAQ";
import CDxGovernanceCTA from "@/components/blog/CDxGovernanceCTA";

import BlogPostContent from "@/components/content/BlogPostContent";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ContentGate from "@/components/blog/ContentGate";
import { supabase } from "@/integrations/supabase/client";
import { cleanBlogContent } from "@/lib/cleanBlogContent";
import { injectInternalLinks } from "@/lib/internalLinks";
import { authorSlug, authorDisplayName, parseAuthors } from "@/lib/authorSlug";
import { useBlogTranslation } from "@/contexts/BlogTranslationContext";

const CDX_CTA_SLUGS: Record<string, "governance" | "alignment" | "monitoring"> = {
  "ivdr-sponsor-cdx-performance-study": "governance",
  "promotor-ivdr-estudio-funcionamiento-cdx": "governance",
  "cdx-submission-ctr-ivdr-alignment": "alignment",
  "alineacion-ctr-ivdr-presentacion-cdx": "alignment",
  "cdx-site-agreement-ivdr-monitoring": "monitoring",
  "acuerdo-centro-cdx-monitorizacion-ivdr": "monitoring",
};

// ES post where the "Future of Medical Information" webinar CTA is injected
// inline, just before the closing "QbD Group apoya…" paragraph.
const WEBINAR_CTA_SLUG =
  "por-que-los-volumenes-de-consultas-medicas-estan-aumentando-y-por-que-tu-modelo-actual-de-im-podria-no-estar-preparado-para-ello";
const WEBINAR_CTA_SPLIT_MARK = "QbD Group apoya a compañías";
const WEBINAR_CTA_EN_SLUG = "rising-medical-inquiry-volumes-mi-model";
const WEBINAR_CTA_EN_SPLIT_MARK = "QbD Group supports pharmaceutical";

// ES Annex 1 CCS post: the webinar CTA is injected twice inline — once
// before "¿Qué debe contener una CCS práctica?" and once before the closing
// "En resumen" section.
const ANNEX1_CTA_SLUG =
  "estrategia-de-control-de-la-contaminacion-ccs-y-procesos-asepticos-anexo-1";
const ANNEX1_CTA_SPLIT_MARKS = [
  "## ¿Qué debe contener una CCS práctica?",
  "## En resumen",
];
const ANNEX1_CTA_SPLIT_MARK = ANNEX1_CTA_SPLIT_MARKS[1];
const ANNEX1_CTA_URL = "/es/webinars/ccs-qc-qa-regulatory-control-contaminacion-anexo-1";

const tailProseClasses =
  "prose prose-lg max-w-none prose-p:text-foreground/80 prose-p:leading-[1.85] prose-p:text-[1.0625rem] prose-a:text-qbd-blue prose-strong:text-foreground";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  const lang: SiteLang = pathname.startsWith("/zh-cn")
    ? "zh-cn"
    : pathname.startsWith("/es")
      ? "es"
      : "en";
  const blogPrefix =
    lang === "zh-cn" ? "/zh-cn/blog" : lang === "es" ? "/es/blog" : "/en/blog";
  const t = uiT(lang);

  // Chinese posts are stored with a "-zh" slug suffix; the public URL drops it
  // since the language is already in the path prefix.
  const needsSlugRedirect = lang === "zh-cn" && !!slug && /-zh$/.test(slug);

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug, lang, isPreview],
    queryFn: async () => {
      // 1) try exact slug + lang
      let exactQuery = supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug!)
        .eq("lang", lang);
      if (!isPreview) exactQuery = exactQuery.not("published_at", "is", null);
      const exact = await exactQuery.maybeSingle();
      if (exact.data) return exact.data;

      // 2) zh-cn fallback: maybe the URL uses the English slug — find EN post,
      //    then look up its zh-cn translation
      if (lang === "zh-cn") {
        const en = await supabase
          .from("blog_posts")
          .select("id")
          .eq("slug", slug!)
          .eq("lang", "en")
          .maybeSingle();
        if (en.data) {
          let zhQuery = supabase
            .from("blog_posts")
            .select("*")
            .eq("translation_of", en.data.id)
            .eq("lang", "zh-cn");
          if (!isPreview) zhQuery = zhQuery.not("published_at", "is", null);
          const zh = await zhQuery.maybeSingle();
          if (zh.data) return zh.data;
        }
      }

      if (exact.error) throw exact.error;
      return null;
    },
    enabled: !!slug,
  });


  const cleanedContent = post?.content ? injectInternalLinks(cleanBlogContent(post.content)) : "";

  const checklistCta =
    lang === "es" &&
    post?.slug === "los-5-errores-mas-comunes-en-los-proyectos-de-cualificacion-termica" ? (
      <InlineCTA ctaUrl="/es/checklist-cualificacion-sistemas-termicos-gmp" slug={post.slug} />
    ) : undefined;
  const CHECKLIST_SECTION_HEADING =
    "La cualificación térmica exitosa requiere una mentalidad de ciclo de vida";

  // White paper CTA: rendered right after the section named here, so it lands
  // immediately before the section that follows it.
  const COMBINED_STUDIES_CTA_AFTER: Record<string, string> = {
    "ivdr-sponsor-cdx-performance-study": "When Nobody Owns IVDR Sponsorship",
    "cdx-submission-ctr-ivdr-alignment":
      "What CDx Reviewers Actually See, and What They Don't",
    "cdx-site-agreement-ivdr-monitoring":
      "Reason Two: Nobody Double-Checked the Sample Collection Site",
  };

  const combinedStudiesCta =
    lang === "en" && post?.slug && COMBINED_STUDIES_CTA_AFTER[post.slug] ? (
    <aside aria-label="Combined Studies White Paper" className="overflow-hidden rounded-lg border border-qbd-sky bg-qbd-ice">
      <div className="grid sm:grid-cols-[220px_1fr]">
        <img
          src="https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/white-papers/collaborative-responsibilities-combined-studies-hero-v2.jpg"
          alt="Collaborative Responsibilities in Combined Studies white paper"
          loading="lazy"
          className="h-48 w-full object-cover sm:h-full"
        />
        <div className="flex flex-col items-start justify-center p-6 sm:p-8">
          <p className="mb-2 text-xs font-semibold uppercase text-primary">White Paper</p>
          <h3 className="mb-3 text-xl font-bold text-foreground">Collaborative Responsibilities in Combined Studies</h3>
          <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
            Explore how regulatory, quality, legal and operational teams can align responsibilities when clinical trials and IVDR performance studies run side by side.
          </p>
          <Button asChild className="h-auto whitespace-normal py-3 text-left">
            <Link to="/en/downloads/collaborative-responsibilities-in-combined-studies">
              <Download aria-hidden="true" /> Download the White Paper
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  ) : undefined;
  const articleInlineCta = checklistCta ?? combinedStudiesCta;
  const articleInlineCtaHeading =
    combinedStudiesCta && post ? COMBINED_STUDIES_CTA_AFTER[post.slug] : CHECKLIST_SECTION_HEADING;

  // Fetch the translated post for the Navbar language switcher
  const { data: translatedPost } = useQuery({
    queryKey: ["blog-translation", post?.id, post?.lang, post?.translation_of],
    queryFn: async () => {
      if (post!.translation_of) {
        const { data } = await supabase
          .from("blog_posts")
          .select("slug, lang")
          .eq("id", post!.translation_of)
          .maybeSingle();
        return data;
      } else {
        const { data } = await supabase
          .from("blog_posts")
          .select("slug, lang")
          .eq("translation_of", post!.id)
          .maybeSingle();
        return data;
      }
    },
    enabled: !!post,
  });

  const { setTranslation } = useBlogTranslation();

  useEffect(() => {
    if (translatedPost) {
      const prefix =
        translatedPost.lang === "zh-cn"
          ? "/zh-cn/blog"
          : translatedPost.lang === "es"
            ? "/es/blog"
            : "/en/blog";
      const label =
        translatedPost.lang === "zh-cn"
          ? "中文"
          : translatedPost.lang === "es"
            ? "ES"
            : "EN";
      setTranslation({
        lang: label as any,
        path: `${prefix}/${publicBlogSlug(translatedPost.slug, prefix)}`,
      });

    } else {
      setTranslation(null);
    }
    return () => setTranslation(null);
  }, [translatedPost, setTranslation]);



  // Fetch author profile for enriched SEO
  const aSlug = post?.author ? authorSlug(post.author) : null;
  const { data: authorProfile } = useQuery({
    queryKey: ["author-profile", aSlug],
    queryFn: async () => {
      const { data } = await supabase
        .from("authors")
        .select("slug, linkedin_url")
        .eq("slug", aSlug!)
        .maybeSingle();
      return data;
    },
    enabled: !!aSlug,
  });

  const wordCount = cleanedContent
    ? cleanedContent.split(/\s+/).length
    : 0;
  const readingTime = wordCount ? Math.max(1, Math.ceil(wordCount / 250)) : 0;

  if (needsSlugRedirect) {
    return (
      <Navigate
        to={`${blogPrefix}/${slug!.replace(/-zh$/, "")}${window.location.search}`}
        replace
      />
    );
  }

  return (

    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />

      {isLoading ? (
        <ContentLoadingState />
      ) : !post ? (
        <div className="pt-36 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground">{t("postNotFound")}</h1>
          <Link
            to={blogPrefix}
            className="mt-4 inline-flex items-center gap-1 text-qbd-blue font-semibold"
          >
            ← {t("backToBlog")}
          </Link>
        </div>
      ) : (
        <>
          <BlogSEO
            type="post"
            title={post.title}
            metaTitle={post.slug === "eudravigilance-signal-management-approaches"
              ? "EudraVigilance Signal Management: Choosing Your Approach | QbD Group"
              : post.slug === "eudravigilance-gestion-senales-enfoques"
                ? "Gestión de Señales con EudraVigilance: Elige tu Enfoque | QbD Group"
                : post.slug === "eudravigilance-signal-management-approaches-zh"
                  ? "EudraVigilance 信号管理：选择适合的方法 | QbD Group"
                  : post.slug === "eu-variations-grouping-worksharing-guide"
                    ? "EU Variations: A Practical Guide to Grouping and Worksharing | QbD Group"
                    : post.slug === "variaciones-ue-grouping-worksharing"
                      ? "Variaciones en la UE: Guía Práctica sobre Grouping y Worksharing | QbD Group"
                      : post.slug === "eu-variations-grouping-worksharing-guide-zh"
                        ? "欧盟变更：分组与工作分享实用指南 | QbD Group"
                        : post.slug === "capa-backlog-management-inspection-readiness"
                          ? "CAPA Backlog Management: From Triage to Inspection Readiness | QbD Group"
                          : post.slug === "gestion-capa-pendientes-preparacion-inspecciones"
                            ? "Gestión de CAPA Pendientes: De la Priorización a la Preparación para Inspecciones | QbD Group"
                            : post.slug === "capa-backlog-management-inspection-readiness-zh"
                              ? "CAPA 积压管理：从风险分级到检查准备 | QbD Group"
                              : undefined}
            description={post.summary ?? undefined}
            slug={publicBlogSlug(post.slug, blogPrefix)}
            image={post.featured_image}
            author={post.author ? authorDisplayName(post.author) : undefined}
            authorSlug={authorProfile?.slug}
            authorLinkedin={authorProfile?.linkedin_url}
            publishedAt={post.published_at}
            updatedAt={post.content_updated_at}
            category={post.category}
            tags={post.tags}
            basePath={blogPrefix}
          />

          <ReadingProgressBar />

          <BlogPostHero
            title={post.title}
            category={post.category}
            publishedAt={post.published_at}
            updatedAt={post.content_updated_at}
            author={post.author}
            featuredImage={post.featured_image}
            readingTime={readingTime}
            summary={post.summary}
            tags={post.tags}
            postId={post.id}
            postLang={post.lang}
            translationOf={post.translation_of}
          />

          {/* Content */}
          <section className="py-16 sm:py-24 bg-background">
            <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
              <div className="flex gap-16">
                {/* Article body */}
                <article className={`flex-1 min-w-0 max-w-3xl mx-auto xl:mx-0 ${
                  (post.slug === "eu-notified-body-process-medical-devices" ||
                    post.slug === "eu-mdr-clinical-evidence-medical-devices" ||
                    post.slug === "clinical-strategy-eu-mdr-market-access" ||
                    post.slug === "clinical-strategy-eu-mdr-market-access-zh" ||
                    post.slug === "eu-mdr-operational-requirements-market-entry" ||
                    post.slug === "eu-mdr-operational-requirements-market-entry-zh" ||
                    post.slug === "ai-validation-pharma-medtech-lessons" ||
                    post.slug === "ai-compliance-framework-life-sciences" ||
                    post.slug === "un-modelo-de-7-fases-para-la-conformidad-de-ia-ml-en-life-sciences")
                    ? "[&_.prose_ul]:!bg-[hsl(210_60%_96%)] [&_.prose_ul]:!border [&_.prose_ul]:!border-[hsl(210_50%_88%)] [&_.prose_ul]:!rounded-2xl [&_.prose_ul]:!py-5 [&_.prose_ul]:!pl-10 [&_.prose_ul]:!pr-6 [&_.prose_ul]:!my-8 [&_.prose_ul_li::marker]:!text-primary"
                    : ""
                }`}>
                  {/* Listen button */}
                  <div className="mb-8">
                    <ListenButton content={cleanedContent} title={post.title} />
                  </div>

                  {post.slug === "scaling-pharmacovigilance-europe" ||
                  post.slug === "oncology-ivd-clinical-study-design-mistakes" ? (
                    <BlogPostContent
                      content={cleanedContent}
                      category={post.category}
                      showDropCap={true}
                      featuredImage={post.featured_image}
                      afterSection={articleInlineCta}
                      afterSectionHeading={articleInlineCtaHeading}
                    />
                  ) : (
                    <ContentGate
                      wordCount={wordCount}
                      content={cleanedContent}
                      renderContent={(md, dropCap) => {
                        const webinarCtaUrl =
                          lang === "es" && post.slug === WEBINAR_CTA_SLUG
                            ? "/es/webinars/the-future-of-medical-information"
                            : lang === "en" && post.slug === WEBINAR_CTA_EN_SLUG
                              ? "/en/webinars/the-future-of-medical-information"
                              : null;
                        const annex1CtaUrl =
                          lang === "es" && post.slug === ANNEX1_CTA_SLUG ? ANNEX1_CTA_URL : null;
                        const inlineCtaUrl = webinarCtaUrl ?? annex1CtaUrl;
                        const splitMark = webinarCtaUrl
                          ? lang === "es"
                            ? WEBINAR_CTA_SPLIT_MARK
                            : WEBINAR_CTA_EN_SPLIT_MARK
                          : annex1CtaUrl
                            ? ANNEX1_CTA_SPLIT_MARK
                            : null;
                        const splitIdx = splitMark ? md.indexOf(splitMark) : -1;
                        let tailText = "";
                        let refsMd = "";
                        if (webinarCtaUrl && splitIdx > 0) {
                          const tail = md.slice(splitIdx);
                          const refsMatch = tail.match(/(^|\n)#{2,3}\s*(fuentes|references|sources|referencias)\b/i);
                          const cut = refsMatch ? splitIdx + (refsMatch.index ?? 0) + (refsMatch[1] ? 1 : 0) : md.length;
                          tailText = md.slice(splitIdx, cut);
                          refsMd = cut < md.length ? md.slice(cut) : "";
                        }
                        // Annex 1 CCS post: two inline CTA placements, one
                        // before "¿Qué debe contener una CCS práctica?" and
                        // one before the closing "En resumen" section.
                        const annex1Idx = annex1CtaUrl
                          ? ANNEX1_CTA_SPLIT_MARKS.map((m) => md.indexOf(m))
                          : [];
                        const annex1Valid =
                          annex1CtaUrl !== null &&
                          annex1Idx.length === 2 &&
                          annex1Idx[0] > 0 &&
                          annex1Idx[1] > annex1Idx[0];
                        if (annex1Valid) {
                          return (
                            <>
                              <BlogPostContent
                                content={md.slice(0, annex1Idx[0])}
                                category={post.category}
                                showDropCap={dropCap}
                                featuredImage={post.featured_image}
                                afterSection={articleInlineCta}
                                afterSectionHeading={articleInlineCtaHeading}
                              />
                              <InlineCTA ctaUrl={annex1CtaUrl} slug={post.slug} />
                              <BlogPostContent
                                content={md.slice(annex1Idx[0], annex1Idx[1])}
                                category={post.category}
                                showDropCap={false}
                                featuredImage={post.featured_image}
                              />
                              <InlineCTA ctaUrl={annex1CtaUrl} slug={post.slug} />
                              <BlogPostContent
                                content={md.slice(annex1Idx[1])}
                                category={post.category}
                                showDropCap={false}
                                featuredImage={post.featured_image}
                              />
                            </>
                          );
                        }
                        return (
                          <>
                            <BlogPostContent
                              content={splitIdx > 0 ? md.slice(0, splitIdx) : md}
                              category={post.category}
                              showDropCap={dropCap}
                              featuredImage={post.featured_image}
                              serviceCtaImage={["ivd-software-risk-class-clinical-evidence", "software-ivd-clase-riesgo-evidencia-clinica"].includes(post.slug) ? post.featured_image : undefined}
                              afterSection={articleInlineCta}
                              afterSectionHeading={articleInlineCtaHeading}
                            />
                            {splitIdx > 0 && inlineCtaUrl && (
                              <>
                                <InlineCTA
                                  ctaUrl={inlineCtaUrl}
                                  slug={post.slug}
                                />
                                {webinarCtaUrl ? (
                                  <>
                                    <div className={tailProseClasses}>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                        {tailText}
                                      </ReactMarkdown>
                                    </div>
                                    {refsMd && (
                                      <BlogPostContent
                                        content={refsMd}
                                        category={post.category}
                                        showDropCap={false}
                                        featuredImage={post.featured_image}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <BlogPostContent
                                    content={md.slice(splitIdx)}
                                    category={post.category}
                                    showDropCap={false}
                                    featuredImage={post.featured_image}
                                  />
                                )}
                              </>
                            )}
                          </>
                        );
                      }}
                    />
                  )}

                  {/* FAQ accordion + JSON-LD */}
                  <BlogFAQ slug={post.slug} />


                  {/* Contextual CTA */}
                  {CDX_CTA_SLUGS[post.slug] ? (
                    <CDxGovernanceCTA lang={post.lang as SiteLang} variant={CDX_CTA_SLUGS[post.slug]} />
                  ) : !post.tags?.includes("_no-cta") &&
                    !["ivd-software-risk-class-clinical-evidence", "software-ivd-clase-riesgo-evidencia-clinica"].includes(post.slug) && (
                    <InlineCTA
                      category={post.service_category || post.category}
                      ctaLabel={(post as any).cta_label}
                      ctaUrl={(post as any).cta_url}
                      slug={post.slug}
                    />
                  )}

                  {/* Secondary CTA — IVD clinical performance studies */}
                  {post.slug === "intended-purpose-oncology-ivd-development" && (
                    <InlineCTA
                      ctaUrl="/en/services/clinical-solutions/ivd-clinical-performance-studies"
                      slug={post.slug}
                    />
                  )}

                  {/* Author bio box(es) */}
                  {post.author && (() => {
                    const authors = parseAuthors(post.author);
                    return (
                      <div className="mt-16 space-y-6">
                        {authors.map(a => (
                          <AuthorBioBox key={authorSlug(a)} authorRaw={a} />
                        ))}
                      </div>
                    );
                  })()}

                  {/* Share buttons */}
                  <div className="mt-12 pt-8 border-t border-border/50">
                    <ShareButtons
                      url={`${blogPrefix}/${post.slug}`}
                      title={post.title}
                    />
                  </div>

                  {/* Newsletter — only on non-gated posts (gated ones already have the gate signup) */}
                  {(wordCount < 1000 ||
                    post.slug === "scaling-pharmacovigilance-europe" ||
                    post.slug === "oncology-ivd-clinical-study-design-mistakes") && (
                    <div className="mt-16">
                      <NewsletterSignup />
                    </div>
                  )}
                </article>

                {/* TOC sidebar — desktop (right side) */}
                {cleanedContent && (
                  <aside className="hidden lg:block w-64 shrink-0">
                    <div className="sticky top-36 space-y-6">
                      <TableOfContents content={cleanedContent} lang={post.lang} />
                    </div>
                  </aside>
                )}
              </div>
            </div>
          </section>

          {/* Related posts */}
          <RelatedPosts currentId={post.id} category={post.category} lang={lang} />
        </>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
